const FAILED_PAGE_LOADS_KEY = 'cip.syncMessageSw.failedPageLoads';

/** This page load's count, once it has been counted. */
let countThisPageLoad: number | null = null;

/**
 * Counts this page load as one where the sync-message service worker failed to
 * start, and returns how many such loads this tab has had. Only the first call
 * per page load increments, so the boot warm-up and the code runner reporting
 * the same failure count it once. sessionStorage keeps the count across
 * reloads of the tab; a new tab starts again at 1.
 *
 * @return The number of failed page loads in this tab, including this one; 1
 *   when sessionStorage is unavailable.
 * @throws Never — storage failures are logged at debug and treated as a first
 *   failure.
 */
export function countFailedPageLoad(): number {
  if (countThisPageLoad !== null) {
    return countThisPageLoad;
  }

  try {
    const previous = Number(sessionStorage.getItem(FAILED_PAGE_LOADS_KEY) ?? 0);
    countThisPageLoad = (Number.isFinite(previous) ? previous : 0) + 1;
    sessionStorage.setItem(FAILED_PAGE_LOADS_KEY, String(countThisPageLoad));
  } catch (error) {
    console.debug('[SyncMessage SW] Failed-load count unavailable', error);
    countThisPageLoad = 1;
  }
  return countThisPageLoad;
}

/**
 * Forgets this tab's failed page loads once the service worker has started, so
 * a later failure is treated as a first failure again.
 *
 * @return Nothing.
 * @throws Never — storage failures are logged at debug.
 */
export function clearFailedPageLoads(): void {
  countThisPageLoad = null;
  try {
    sessionStorage.removeItem(FAILED_PAGE_LOADS_KEY);
  } catch (error) {
    console.debug('[SyncMessage SW] Failed-load count not cleared', error);
  }
}
